import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  TextInput,
  Stack,
  Group,
  Text,
  Avatar,
  Badge,
  UnstyledButton,
  Loader,
  Center,
  Anchor,
  Box,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Search, ExternalLink } from 'react-feather';
import { billcomApi } from '../../../../services/api/billcom';
import type { BillComVendorMatch } from '../../../../services/api/billcom';
import { env } from '../../../../config/env';

interface LinkVendorModalProps {
  opened: boolean;
  onClose: () => void;
  coachUserId: string;
  coachName: string;
  coachEmail: string;
  onLinked: () => void;
}

export function LinkVendorModal({
  opened,
  onClose,
  coachUserId,
  coachName,
  coachEmail,
  onLinked,
}: LinkVendorModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(searchQuery, 400);
  const [vendors, setVendors] = useState<BillComVendorMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [linking, setLinking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Load suggested matches when modal opens
  const loadSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      const results = await billcomApi.getSuggestedMatches(coachUserId);
      setVendors(results);
      setSuggestionsLoaded(true);
    } catch {
      // Silently fail — user can search manually
    } finally {
      setLoading(false);
    }
  }, [coachUserId]);

  useEffect(() => {
    if (opened && !suggestionsLoaded) {
      loadSuggestions();
    }
  }, [opened, suggestionsLoaded, loadSuggestions]);

  // Search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      if (suggestionsLoaded && !debouncedQuery) {
        loadSuggestions();
      }
      return;
    }

    const search = async () => {
      try {
        setLoading(true);
        const results = await billcomApi.searchVendors(debouncedQuery, coachEmail);
        setVendors(results);
      } catch {
        notifications.show({
          title: 'Search Error',
          message: 'Failed to search Bill.com vendors',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };

    search();
  }, [debouncedQuery, coachEmail, suggestionsLoaded, loadSuggestions]);

  const handleSelectVendor = async (vendor: BillComVendorMatch) => {
    try {
      setLinking(true);
      await billcomApi.linkVendor(coachUserId, vendor.id, vendor.name);
      notifications.show({
        title: 'Vendor Linked',
        message: `${coachName} linked to ${vendor.name}`,
        color: 'green',
      });
      onLinked();
      handleClose();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to link vendor',
        color: 'red',
      });
    } finally {
      setLinking(false);
    }
  };

  // MED-1174: manual "Refresh now" — bust the 15-min server cache and re-run whatever
  // view is active (suggestions when the box is empty, search otherwise).
  const handleRefresh = async () => {
    const query = debouncedQuery.trim();
    try {
      setRefreshing(true);
      setLoading(true);
      const results =
        query.length >= 2
          ? await billcomApi.searchVendors(query, coachEmail, true)
          : await billcomApi.getSuggestedMatches(coachUserId, true);
      setVendors(results);
    } catch {
      notifications.show({
        title: 'Refresh Error',
        message: 'Failed to refresh the vendor list',
        color: 'red',
      });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setVendors([]);
    setSuggestionsLoaded(false);
    onClose();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={700} size="lg">
          Link Bill.com Vendor
        </Text>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Subtitle */}
        <Text size="sm" c="dimmed">
          Link <Text span fw={700} c="dark">{coachName}</Text> to a vendor record in Bill.com for
          payment processing.
        </Text>

        {/* Search Input */}
        <TextInput
          placeholder="Search Bill.com vendors by name or email..."
          leftSection={<Search size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          autoFocus
        />

        {/* Cache-staleness disclosure + manual refresh (MED-1174) */}
        <Group justify="space-between" gap="xs" wrap="nowrap" mt={-8}>
          <Text size="xs" c="dimmed">
            Vendor list refreshes every 15 min — newly added Bill.com vendors may take a few
            minutes to appear.
          </Text>
          <Anchor
            component="button"
            type="button"
            size="xs"
            fw={600}
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ whiteSpace: 'nowrap' }}
          >
            Refresh now
          </Anchor>
        </Group>

        {/* Results */}
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : vendors.length > 0 ? (
          <Stack gap={0}>
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: '0.3px' }}
              mb="xs"
            >
              {searchQuery ? 'Search Results' : 'Suggested Matches'}
            </Text>

            <Box
              style={{
                border: '1px solid #E8E6E1',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {vendors.map((vendor, idx) => (
                <UnstyledButton
                  key={vendor.id}
                  onClick={() => handleSelectVendor(vendor)}
                  disabled={linking}
                  w="100%"
                  p="md"
                  style={{
                    borderTop: idx > 0 ? '1px solid #E8E6E1' : undefined,
                    '&:hover': { backgroundColor: '#F5F3EF' },
                  }}
                  styles={{
                    root: {
                      '&:hover': { backgroundColor: '#F5F3EF' },
                    },
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <Avatar radius="xl" size="md" color="gray">
                        {getInitials(vendor.name)}
                      </Avatar>
                      <div>
                        <Group gap="xs">
                          <Text fw={700} size="sm">
                            {vendor.name}
                          </Text>
                          {vendor.isExactMatch && (
                            <Badge
                              size="xs"
                              variant="outline"
                              color="medleyGreen"
                              tt="uppercase"
                              fw={600}
                            >
                              Exact Match
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {vendor.email}
                        </Text>
                      </div>
                    </Group>
                    <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {vendor.id}
                    </Text>
                  </Group>
                </UnstyledButton>
              ))}
            </Box>
          </Stack>
        ) : (
          suggestionsLoaded && (
            <Text c="dimmed" ta="center" py="md" size="sm">
              No vendors found. Try a different search term.
            </Text>
          )
        )}

        {/* Fallback: Add Vendor in Bill.com */}
        <Box
          p="md"
          ta="center"
          style={{
            border: '2px dashed #E8E6E1',
            borderRadius: 8,
          }}
        >
          <Anchor
            href={env.billcomVendorsUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="never"
            c="dark"
            fw={500}
            size="sm"
          >
            <Group gap="xs" justify="center">
              <ExternalLink size={16} />
              <span>Add Vendor in Bill.com</span>
            </Group>
          </Anchor>
          <Text size="xs" c="dimmed" mt="xs">
            This coach doesn&apos;t exist in Bill.com yet.
            <br />
            Add them in Bill.com, then search here to link — new vendors can take up to 15 min to
            appear.
          </Text>
        </Box>
      </Stack>
    </Modal>
  );
}
